import { useState, useEffect } from "react";
import Select from "react-select";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Filter, RotateCcw } from "lucide-react";
import Campo from "../posgrados/Campo";
import Modalidad from "../posgrados/Modalidad";

const BASE_URL = "https://be-ofertas.utpl.edu.ec";

const programaMap = {
  maestria: "Maestría",
  especizacion: "Especialización",
  doctorado: "Doctorado",
};

const modalidadMap = {
  en_linea: "En línea",
  presencial: "Presencial",
  hibrida: "Híbrida",
};

const facultadMap = {
  facultad_de_ciencias_economicas_y_empresariales:
    "Ciencias Económicas y Empresariales",
  facultad_de_ciencias_exactas_y_naturales: "Ciencias Exactas y Naturales",
  facultad_de_ciencias_de_la_salud: "Ciencias de la Salud",
  facultad_de_ciencias_del_comportamiento: "Ciencias del Comportamiento",
  facultad_de_ciencias_sociales_educacion_y_humanidades:
    "Ciencias Sociales, Educación y Humanidades",
  facultad_de_ciencias_juridicias_y_politicas:
    "Ciencias Jurídicas y Políticas",
  facultad_de_ingenierias_y_arquitectura: "Ingenierías y Arquitectura",
  escuela_de_negocios: "UTPL Escuela de Negocios",
};

const optionsFacultad = [
  { value: "todos", label: "Todas las facultades" },
  {
    value: "Facultad de Ciencias Económicas y Empresariales",
    label: "Ciencias Económicas y Empresariales",
  },
  {
    value: "Facultad de Ciencias del Comportamiento",
    label: "Ciencias del Comportamiento",
  },
  {
    value: "Facultad de Ciencias Exactas y Naturales",
    label: "Ciencias Exactas y Naturales",
  },
  {
    value: "Facultad de Ciencias Sociales, Educación y Humanidades",
    label: "Ciencias Sociales, Educación y Humanidades",
  },
  {
    value: "Facultad de Ciencias Jurídicas y Políticas",
    label: "Ciencias Jurídicas y Políticas",
  },
  {
    value: "Facultad de Ciencias de la Salud",
    label: "Ciencias de la Salud",
  },
  {
    value: "Facultad de Ingenierías y Arquitectura",
    label: "Ingenierías y Arquitectura",
  },
];

const badgeColors = {
  Maestría: "bg-blue-100 text-blue-800 border border-blue-200",
  Especialización: "bg-purple-100 text-purple-800 border border-purple-200",
  Doctorado: "bg-emerald-100 text-emerald-800 border border-emerald-200",
};

const formatearPrueba = (texto) => {
  const formatos = {
    prueba_diagnostica: {
      nombre: "Prueba diagnóstica",
      clases: "text-green-600 bg-green-50",
    },
    prueba_de_admision: {
      nombre: "Prueba de admisión",
      clases: "text-amber-600 bg-amber-50",
    },
  };

  return (
    formatos[texto?.toLowerCase()] || {
      nombre: texto,
      clases: "text-[#004270] bg-blue-50 border-[#004270]/20",
    }
  );
};

const cleanText = (text) => {
  if (!text) return "";

  return text
    .toString()
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const getImageUrl = (node, included, fieldName) => {
  const mediaId = node.relationships?.[fieldName]?.data?.id;

  if (!mediaId) return null;

  const media = included.find(
    (item) => item.type === "media--image" && item.id === mediaId
  );

  const fileId = media?.relationships?.field_media_image?.data?.id;

  if (!fileId) return null;

  const file = included.find(
    (item) => item.type === "file--file" && item.id === fileId
  );

  return file?.attributes?.uri?.url || null;
};

const getInitialValue = (param, options) => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(param);

  return options.find((o) => o.value === value) || options[0];
};

function OfferPresencial() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [facultadSeleccionada, setFacultadSeleccionada] = useState(
    getInitialValue("facultad", optionsFacultad)
  );

  // Petición de datos
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const baseUrl =
          "https://be-ofertas.utpl.edu.ec/jsonapi/node/carrera?" +
          "filter[field_modalidad]=presencial" +
          "&fields[node--carrera]=title,field_etiqueta_presencial,field_presencial_tipo_prueba,field_tipo_de_programa,field_facultad,field_modalidad,field_duracion,field_inicio_clases,field_campo_amplio,field_url_al_contenido,field_imagen_portada,field_etiqueta_enlinea" +
          "&fields[media--image]=field_media_image" +
          "&fields[file--file]=uri" +
          "&include=field_imagen_portada,field_imagen_portada.field_media_image";

        const urls = [
          `${baseUrl}`,
          `${baseUrl}&page[offset]=50&page[limit]=50`,
        ];

        const responses = await Promise.all(urls.map((url) => fetch(url)));
        const jsons = await Promise.all(responses.map((r) => r.json()));

        const allItems = jsons.flatMap((json) => json.data || []);
        const allIncluded = jsons.flatMap((json) => json.included || []);

        const uniqueItems = [
          ...new Map(allItems.map((item) => [item.id, item])).values(),
        ];

        const transformed = uniqueItems.map((item) => {
          const attrs = item.attributes;

          const imagePath = getImageUrl(
            item,
            allIncluded,
            "field_imagen_portada"
          );

          let modalidadFormateada = attrs.field_modalidad;
          if (Array.isArray(attrs.field_modalidad)) {
            modalidadFormateada = attrs.field_modalidad.map(
              (m) => modalidadMap[m] || m
            );
          } else if (typeof attrs.field_modalidad === "string") {
            modalidadFormateada =
              modalidadMap[attrs.field_modalidad] || attrs.field_modalidad;
          }

          return {
            id: item.id,
            title: attrs.title,
            field_programa:
              programaMap[attrs.field_tipo_de_programa] ||
              attrs.field_tipo_de_programa,
            field_facultad:
              facultadMap[attrs.field_facultad] || attrs.field_facultad,
            field_modalidad: modalidadFormateada,
            etiqueta: attrs.field_etiqueta_presencial || "",
            field_duracion: attrs.field_duracion || "",
            field_inicio: attrs.field_inicio_clases || "",
            field_inicio_clases: attrs.field_inicio_clases || "",
            field_campo_amplio: attrs.field_campo_amplio || "",
            prueba: attrs.field_presencial_tipo_prueba || "",
            field_url: attrs.field_url_al_contenido || "",
            field_imagen_portada: imagePath
              ? BASE_URL + imagePath
              : "https://via.placeholder.com/600x400",
          };
        });

        setData(transformed);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Actualizar URL
  useEffect(() => {
    const params = new URLSearchParams();

    if (facultadSeleccionada.value !== "todos") {
      params.set("facultad", facultadSeleccionada.value);
    }

    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");

    window.history.replaceState({}, "", newUrl);
  }, [facultadSeleccionada]);

  const limpiarFiltros = () => {
    setFacultadSeleccionada(optionsFacultad[0]);
  };

  if (loading) return <p>Cargando...</p>;
  if (error) return <p>Error: {error}</p>;

  // FILTRAR POR FACULTAD
  const filtered = data.filter((item) => {
    if (facultadSeleccionada.value === "todos") {
      return true;
    }

    if (!item.field_facultad) {
      return false;
    }

    const facultadItem = cleanText(item.field_facultad);
    const facultadSeleccionadaLimpia = cleanText(facultadSeleccionada.value);

    return (
      facultadItem === facultadSeleccionadaLimpia ||
      facultadItem.includes(facultadSeleccionadaLimpia) ||
      facultadSeleccionadaLimpia.includes(facultadItem)
    );
  });

  // ORDENAR LOS DATOS
  const filteredData = [...filtered].sort((a, b) => {
    const hasEtiquetaA = Boolean(a.etiqueta?.trim());
    const hasEtiquetaB = Boolean(b.etiqueta?.trim());

    if (!hasEtiquetaA && hasEtiquetaB) return -1;
    if (hasEtiquetaA && !hasEtiquetaB) return 1;

    if (hasEtiquetaA && hasEtiquetaB) {
      const etiquetaComparison = a.etiqueta.localeCompare(b.etiqueta, "es");
      if (etiquetaComparison !== 0) {
        return etiquetaComparison;
      }
    }

    const titleA = cleanText(a.title);
    const titleB = cleanText(b.title);
    return titleA.localeCompare(titleB, "es");
  });

  return (
    <div className="px-16 md:px-10">
      {/* FILTROS */}
      <div className="wrapper-filtros layout9">
        <div>
          <p>
            <b>Facultad</b>
          </p>

          <Select
            options={optionsFacultad}
            value={facultadSeleccionada}
            onChange={setFacultadSeleccionada}
            isSearchable
            placeholder="Selecciona una facultad"
          />
        </div>

        <div>
          <button
            className="btn-reset-filtros flex items-center gap-2"
            onClick={limpiarFiltros}
          >
            <RotateCcw size={16} />
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* CONTADOR */}
      <div className="mb-6 flex items-center justify-between">
        <p className="flex items-center gap-2 text-gray-700">
          <Filter size={16} />
          <span className="font-bold">{filteredData.length}</span>
          programas encontrados
        </p>
      </div>

      {/* GRID */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {filteredData.map((item) => (
          <motion.a
            key={item.id}
            href={item.field_url}
            rel="noreferrer"
            className="group relative block overflow-hidden rounded-2xl bg-white shadow-md"
            initial="rest"
            animate="rest"
            whileHover="hover"
          >
            {/* IMAGEN */}
            <div className="h-40 overflow-hidden">
              <img
                src={item.field_imagen_portada}
                alt={item.title}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
            </div>

            {/* ICONO */}
            <motion.div
              variants={{
                rest: { scale: 1, opacity: 0.8 },
                hover: { scale: 1.2, opacity: 1 },
              }}
              className="absolute right-4 top-4 rounded-full bg-white/90 p-2 shadow-md backdrop-blur-md"
            >
              <Plus size={12} />
            </motion.div>

            {/* CONTENIDO */}
            <div className="space-y-3 p-5">
              {/* ETIQUETA */}
              {item.etiqueta && (
                <div className="flex flex-wrap gap-2">
                  <span
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                      badgeColors[item.field_programa] ||
                      "border border-gray-200 bg-slate-100 text-[#004270]"
                    }`}
                  >
                    {item.etiqueta}
                  </span>
                </div>
              )}

              {item.prueba &&
                (() => {
                  const prueba = formatearPrueba(item.prueba);

                  return (
                    <span
                      className={`inline-block px-3 py-1 text-xs font-medium rounded-full border ${prueba.clases}`}
                    >
                      {prueba.nombre}
                    </span>
                  );
                })()}

              {/* TÍTULO */}
              <h3 className="p-0 text-base font-bold leading-tight">
                {item.title}
              </h3>

              {/* MODALIDAD */}
              {item.field_modalidad && (
                <div
                  className="
                    [&_p]:inline-flex
                    [&_p]:p-0
                    [&_p]:text-sm
                    [&_p]:leading-none
                    [&_p]:text-gray-600
                    [&_ul]:m-0
                    [&_ul]:p-0
                    [&_li]:py-0
                    [&_li]:pt-1
                    [&_li]:text-sm
                    [&_li]:leading-none
                    [&_li]:text-gray-600
                  "
                >
                  <p>
                    <strong>Modalidad: </strong>
                  </p>

                  <Modalidad value={item.field_modalidad} />
                </div>
              )}

              {/* DURACIÓN */}
              {item.field_duracion && (
                <p className="p-0 text-sm leading-none text-gray-600">
                  <strong>Duración:</strong>{" "}
                  {item.field_duracion
                    .replaceAll("_", " ")
                    .replace("anos", "años")
                    .replace("ano", "año")}
                </p>
              )}

              {/* FACULTAD */}
              {item.field_facultad && (
                <p className="p-0 text-sm leading-none text-gray-600">
                  <strong>Facultad:</strong> {item.field_facultad}
                </p>
              )}

              {/* CAMPO */}
              {item.field_campo_amplio && (
                <p className="p-0 text-sm leading-none text-gray-600">
                  <strong>Campo: </strong>
                  <Campo value={item.field_campo_amplio} />
                </p>
              )}

        {/*       {/* INICIO DE CLASES  
              {item.field_inicio_clases && (
                <p className="p-0 text-sm leading-none text-gray-600">
                  <strong>Inicio de clases:</strong> {item.field_inicio_clases}
                </p>
              )} */}
            </div>

            {/* OVERLAY */}
            <motion.div
              variants={{
                rest: { opacity: 0 },
                hover: { opacity: 1 },
              }}
              className="absolute inset-0 flex items-center justify-center bg-black/10"
            >
              <ArrowRight size={44} className="text-white" />
            </motion.div>
          </motion.a>
        ))}
      </div>
    </div>
  );
}

export default OfferPresencial;